import React, { useState, useEffect } from "react";
import { FiRefreshCw } from "react-icons/fi";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import authAxios from "../../../api/authAxios";

export default function SystemHealth() {
  const { theme } = useTheme();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/system/health/");
      const arr = Object.entries(res.data || {}).map(([name, info]) => ({
        name,
        ...info,
      }));
      setServices(arr);
    } catch (err) {
      console.error("Failed to fetch service health", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await authAxios.get("/system/system-stats/");
      const now = new Date().toLocaleTimeString();
      const d = res.data;
      setStats(d);
      setHistory((prev) => [
        ...prev.slice(-19),
        {
          time: now,
          cpu: d.cpu_percent,
          mem: (d.memory.used / d.memory.total) * 100,
          disk: (d.disk.used / d.disk.total) * 100,
        },
      ]);
    } catch (err) {
      console.error("Failed to fetch system stats", err);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchStats();
    const interval = setInterval(() => {
      fetchStatus();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const card = "bg-white dark:bg-darkCard border border-gray-200 dark:border-gray-700 rounded-xl shadow";

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">System Health</h1>
      <p className="text-gray-600 dark:text-gray-400">
        Monitor worker status and resource usage. Auto-refreshes every 10 seconds.
      </p>

      {/* Services */}
      <div className={`${card} p-4`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Services</h2>
          <button
            onClick={() => { fetchStatus(); fetchStats(); }}
            className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                <th className="py-2">Service</th>
                <th className="py-2">Status</th>
                <th className="py-2">Uptime</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc) => (
                <tr key={svc.name} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-2">{svc.name}</td>
                  <td>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        svc.status === "running"
                          ? "bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100"
                          : "bg-red-100 text-red-700 dark:bg-red-700 dark:text-red-100"
                      }`}
                    >
                      {svc.status}
                    </span>
                  </td>
                  <td>{svc.uptime || "-"}</td>
                  <td>
                    <button
                      onClick={() => alert(`Restart ${svc.name}`)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                    >
                      Restart
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {["CPU", "Memory", "Disk"].map((metric, i) => (
            <div key={metric} className={`${card} p-4 text-center`}>
              <h3 className="text-sm text-gray-500">{metric}</h3>
              <p className="text-2xl font-bold mt-1">
                {metric === "CPU" && `${stats.cpu_percent}%`}
                {metric === "Memory" &&
                  `${(stats.memory.used / 1024 ** 3).toFixed(1)} / ${(stats.memory.total / 1024 ** 3).toFixed(1)} GB`}
                {metric === "Disk" &&
                  `${(stats.disk.used / 1024 ** 3).toFixed(1)} / ${(stats.disk.total / 1024 ** 3).toFixed(1)} GB`}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Usage History */}
{/* Usage History */}
{history.length > 0 && (
  <div className={`${card} p-4`}>
    <h3 className="mb-2 font-semibold text-gray-700 dark:text-gray-200">
      Resource Usage Over Time
    </h3>

    {/* Legend */}
    <div className="flex gap-6 mb-4 text-sm">
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 bg-blue-500 rounded-sm"></span> <span>CPU %</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 bg-green-500 rounded-sm"></span> <span>Memory %</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 bg-amber-500 rounded-sm"></span> <span>Disk %</span>
      </div>
    </div>

    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={history}>
        <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
        <XAxis dataKey="time" />
        <YAxis />
        <Tooltip
          contentStyle={{
            background: theme === "dark" ? "#1f2937" : "#fff",
            borderRadius: "0.5rem",
            border: "none",
          }}
        />
        <Line type="monotone" dataKey="cpu" stroke="#3b82f6" dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="mem" stroke="#10b981" dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="disk" stroke="#f59e0b" dot={false} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  </div>
)}


    </div>
  );
}

